import { useMemo } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import SearchInput from "components/SearchInput";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useSearchTofuWorkspaces from "shared/Stack/useSearchTofuWorkspaces";
import useTofuWorkspacesStore from "shared/Stack/useSearchTofuWorkspaces/store";
import useSearchTofuWorkspacesSuggestions from "shared/Stack/useSearchTofuWorkspacesSuggestions";
import { TOFU_WORKSPACES_FILTER_TYPE } from "shared/Stack/useSearchTofuWorkspacesSuggestions/constants";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import StackWorkspacesTable from "./Table";
import StackWorkspacesUnlockAllButton from "./UnlockAllButton";

const StackWorkspaces = () => {
  const { stack } = useTypedContext(StackContext);

  useTitle(`Workspaces · ${stack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stack.name,
    },
  ]);

  const { workspaces, error, initialLoading, isEmpty, searchIsEmpty, startCursor, endCursor } =
    useSearchTofuWorkspaces({ stackId: stack.id });

  const { filteredCount } = useSearchTofuWorkspacesSuggestions({ stackId: stack.id });

  const { resetCursor } = useTofuWorkspacesStore();

  const hasLockedWorkspaces = useMemo(
    () => workspaces.some((workspace) => workspace.isLocked),
    [workspaces]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const pageIsEmpty = isEmpty && !initialLoading;
  const showTable = !isEmpty && !searchIsEmpty;

  return (
    <>
      <StackHeader />
      <PageInfoHeader title="Workspaces">
        <SearchInput
          placeholder="Search workspaces..."
          filtersOrderSettingsKey={TOFU_WORKSPACES_FILTER_TYPE}
          disabled={pageIsEmpty}
          callback={resetCursor}
        />
        <StackWorkspacesUnlockAllButton
          stackId={stack.id}
          hasLockedWorkspaces={hasLockedWorkspaces}
        />
      </PageInfoHeader>

      <Box direction="column" grow="1" fullWidth>
        {isEmpty && (
          <EmptyState
            padding="large"
            icon={EmptystateSearchNoResultsColored}
            title="No workspaces found"
            caption="This stack doesn't have any workspaces yet."
          />
        )}

        {searchIsEmpty && (
          <EmptyState
            padding="large"
            icon={EmptystateSearchNoResultsColored}
            title="No results"
            caption="No workspaces match your search criteria."
          />
        )}

        {showTable && (
          <StackWorkspacesTable
            workspaces={workspaces}
            loading={initialLoading}
            startCursor={startCursor}
            endCursor={endCursor}
            totalCount={filteredCount}
          />
        )}
      </Box>
    </>
  );
};

export default StackWorkspaces;
